import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useUploadAdminImage } from "../../hooks/useUpload";

export default function ImageUploadPage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState("");

  const uploadMutation = useUploadAdminImage();

  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadedUrl("");
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.warning("Please select an image first.");
      return;
    }

    uploadMutation.mutate(selectedFile, {
      onSuccess: (result) => {
        // 🎯 EXACT MATCH: Target the deeply nested 'file' property from your backend
        const finalUrl = result?.data?.data?.file;

        if (finalUrl) {
          setUploadedUrl(finalUrl);
        } else {
          toast.error("Could not find the URL in the response.");
          console.error("Unexpected response structure:", result);
        }
      },
    });
  };

  const handleCopyUrl = () => {
    if (!uploadedUrl) return;
    navigator.clipboard.writeText(uploadedUrl);
    toast.success("URL copied to clipboard!");
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadedUrl("");
    uploadMutation.reset();
  };

  return (
    <div className="container-fluid py-4 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 fw-bolder text-dark">Image Uploader</h2>
          <p className="text-muted mb-0 small">
            Upload images to generate URLs for products, pledges, or content.
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col-md-7 col-lg-12">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0 fw-bold text-dark">Upload New Image</h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleUpload}>
                {/* File Input */}
                <div className="mb-4">
                  <label className="form-label fw-semibold text-dark">
                    Select Image File
                  </label>
                  <input
                    type="file"
                    className="form-control form-control-lg bg-light"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploadMutation.isPending}
                  />
                </div>

                {/* Image Preview */}
                {previewUrl && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-muted small">
                      Image Preview
                    </label>
                    <div className="text-center p-3 border rounded bg-light">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="img-fluid rounded shadow-sm"
                        style={{ maxHeight: "250px", objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex gap-3 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary px-4 fw-medium flex-grow-1 flex-md-grow-0 mr-2"
                    disabled={!selectedFile || uploadMutation.isPending}
                  >
                    {uploadMutation.isPending ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt mr-2"></i>Upload Image
                      </>
                    )}
                  </button>

                  {selectedFile && !uploadMutation.isPending && (
                    <button
                      type="button"
                      className="btn btn-light border fw-medium px-4"
                      onClick={handleReset}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Polished Result Section */}
                {uploadedUrl && (
                  <div className="mt-4 p-3 border border-success border-opacity-50 rounded bg-success bg-opacity-10">
                    <label className="form-label fw-bold text-success mb-2">
                      <i className="fas fa-check-circle mr-2"></i>
                      Image Uploaded Successfully
                    </label>
                    <div className="input-group shadow-sm">
                      <input
                        type="text"
                        className="form-control"
                        value={uploadedUrl}
                        readOnly
                      />
                      <button
                        className="btn btn-success px-4 fw-medium"
                        type="button"
                        onClick={handleCopyUrl}
                      >
                        <i className="far fa-copy mr-2"></i>Copy
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}